// URL shape and language handling.
//
// Every page lives under a language prefix so each translation has its own
// crawlable, shareable address: /da/produkter/. The site root (/) has no
// language of its own -- it redirects to the visitor's remembered or detected
// language. Home is the language root itself (/da/) rather than /da/hjem/.
//
// The data below lives in site.config.json because the build-time route
// generator (scripts/prerender-routes.mjs) reads the same values from Node,
// where it cannot import this ES module.

import config from './site.config.json';

export const LANGS = config.langs;
export const DEFAULT_LANG = config.defaultLang;
export const PAGES = config.pages;
export const SITE_ORIGIN = config.siteOrigin;

// Slugs are localised so the URL reads naturally in each language. They are
// deliberately ASCII (ueber-uns, a-propos, acerca-de) -- accented paths work
// but get percent-encoded when copied, which looks broken when shared.
export const SLUGS = config.slugs;

// Trailing slashes keep these directory-style, so the S3 website endpoint can
// serve each one as its own index.html (see scripts/prerender-routes.mjs).
export function pathFor(lang, page) {
  const slug = SLUGS[lang][page];
  return slug ? `/${lang}/${slug}/` : `/${lang}/`;
}

export function urlFor(lang, page) {
  return SITE_ORIGIN + pathFor(lang, page);
}

// Returns { lang, page }, or null when the path carries no known language --
// the caller then decides which language to send the visitor to.
export function parsePath(pathname) {
  const [lang, slug] = pathname.split('/').filter(Boolean);
  if (!LANGS.includes(lang)) return null;
  if (!slug) return { lang, page: 'home' };
  const page = PAGES.find((p) => SLUGS[lang][p] === slug);
  return { lang, page: page || 'home' };
}

// True for clicks we should handle ourselves. Modified clicks and middle
// clicks are left to the browser so "open in new tab" keeps working.
export function isPlainClick(event) {
  return (
    !event.defaultPrevented &&
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey
  );
}

const STORAGE_KEY = 'oj-lang';

// localStorage rather than a cookie: nothing server-side needs to read this,
// and it is never sent with requests. Both calls are guarded because Safari
// throws on storage access in private mode rather than returning null.
export function readStoredLang() {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return LANGS.includes(stored) ? stored : null;
  } catch {
    return null;
  }
}

export function storeLang(lang) {
  try {
    window.localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    /* private mode - preference simply will not persist */
  }
}

// First visit: use the browser's preferred languages if we publish one of
// them. Region subtags are ignored, so de-AT matches de. Anything we do not
// have -- Japanese, say -- falls through to English.
export function detectLang() {
  const candidates = window.navigator.languages ||
    (window.navigator.language ? [window.navigator.language] : []);
  for (const tag of candidates) {
    const base = String(tag).toLowerCase().split('-')[0];
    if (LANGS.includes(base)) return base;
  }
  return DEFAULT_LANG;
}
